import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  BarChart3, 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  Star, 
  Target, 
  Award,
  Filter,
  CheckCircle2,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import '../GestionRH.css';

const API = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '').replace(/\/api$/, '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function EvaluationsManagement() {
  const { hasPermission } = usePermissions();
  const [evals, setEvals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [evalRes, empRes] = await Promise.all([
        axios.get(`${API}/rh/evaluations`, { headers: headers() }),
        axios.get(`${API}/rh/employes`, { headers: headers() })
      ]);
      setEvals(evalRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      console.error('Error fetching evaluations:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvals = useMemo(() => {
    return evals.filter(e => 
      `${e.employe?.prenom} ${e.employe?.nom}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [evals, searchTerm]);

  const handleAddEval = () => {
    Swal.fire({
      title: 'Nouvelle Évaluation',
      html: `
        <div class="rh-modal-form">
          <div class="rh-input-group">
            <label>Employé *</label>
            <select id="eval-emp" class="rh-modal-input">
              <option value="">Sélectionner...</option>
              ${employees.map(e => `<option value="${e._id}">${e.prenom} ${e.nom}</option>`).join('')}
            </select>
          </div>
          <div class="rh-form-row">
            <div class="rh-input-group">
              <label>Mois *</label>
              <select id="eval-mois" class="rh-modal-input">
                <option value="1">Janvier</option>
                <option value="2">Février</option>
                <option value="3">Mars</option>
                <option value="4">Avril</option>
                <option value="5">Mai</option>
                <option value="6">Juin</option>
                <option value="7">Juillet</option>
                <option value="8">Août</option>
                <option value="9">Septembre</option>
                <option value="10">Octobre</option>
                <option value="11">Novembre</option>
                <option value="12">Décembre</option>
              </select>
            </div>
            <div class="rh-input-group">
              <label>Année *</label>
              <input id="eval-annee" type="number" class="rh-modal-input" value="2024">
            </div>
          </div>
          <div class="rh-input-group">
            <label>Objectifs Atteints (%)</label>
            <input id="eval-score" type="range" min="0" max="100" class="rh-modal-input" value="80" oninput="document.getElementById('score-val').innerText = this.value + '%'">
            <span id="score-val" style="text-align: right; font-weight: 700; color: var(--rh-blue-dark)">80%</span>
          </div>
          <div class="rh-input-group">
            <label>Commentaires</label>
            <textarea id="eval-comment" class="rh-modal-input" placeholder="Points forts, axes d'amélioration..."></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Enregistrer',
      confirmButtonColor: '#FFD700',
      preConfirm: () => {
        const employe = document.getElementById('eval-emp').value;
        const mois = Number(document.getElementById('eval-mois').value);
        const annee = Number(document.getElementById('eval-annee').value);
        const scoreFinal = Number(document.getElementById('eval-score').value);
        const commentaireRH = document.getElementById('eval-comment').value;

        if (!employe) {
          Swal.showValidationMessage('Sélectionnez un employé');
          return false;
        }
        return { employe, mois, annee, scoreFinal, commentaireRH, statut: 'finalisee' };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(`${API}/rh/evaluations`, result.value, { headers: headers() });
          Swal.fire('Enregistré !', '', 'success');
          fetchData();
        } catch (err) {
          Swal.fire('Erreur', 'Impossible d\'enregistrer l\'évaluation.', 'error');
        }
      }
    });
  };

  const handleDelete = (id) => {
    Swal.fire({ title: 'Supprimer ?', icon: 'warning', showCancelButton: true }).then(async (res) => {
      if (res.isConfirmed) {
        await axios.delete(`${API}/rh/evaluations/${id}`, { headers: headers() });
        fetchData();
      }
    });
  };

  if (loading) return <div className="rh-dashboard-content"><LoadingSpinner /></div>;

  return (
    <div className="animate-fade-in">
      <div className="rh-stats-grid">
        <div className="rh-stat-card">
          <div className="rh-stat-icon-wrapper orange">
            <Award size={24} />
          </div>
          <div className="rh-stat-info">
            <span className="rh-stat-value">84%</span>
            <span className="rh-stat-label">Score moyen global</span>
          </div>
        </div>
        <div className="rh-stat-card">
          <div className="rh-stat-icon-wrapper green">
            <CheckCircle2 size={24} />
          </div>
          <div className="rh-stat-info">
            <span className="rh-stat-value">{evals.length}</span>
            <span className="rh-stat-label">Évaluations ce mois</span>
          </div>
        </div>
      </div>

      <div className="rh-table-card">
        <div className="rh-table-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="rh-hero-icon" style={{ background: 'var(--rh-yellow)', width: '45px', height: '45px', borderRadius: '12px' }}>
              <BarChart3 size={24} color="var(--rh-blue-dark)" />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Évaluations de Performance</h2>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--rh-text-muted)' }}>Suivi des objectifs et KPIs</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div className="rh-search-wrapper" style={{ width: '250px' }}>
              <Search className="rh-icon-search" size={16} />
              <input 
                type="text" 
                className="rh-search-input" 
                placeholder="Rechercher..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="rh-add-btn" onClick={handleAddEval}>
              <Plus size={18} /> Évaluer
            </button>
          </div>
        </div>

        <div className="rh-table-container">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Employé</th>
                <th>Période</th>
                <th>Performance</th>
                <th>Note</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvals.map(ev => (
                <tr key={ev._id}>
                  <td>
                    <div className="rh-user-cell">
                      <div className="rh-avatar">{(ev.employe?.prenom?.[0] || '') + (ev.employe?.nom?.[0] || '')}</div>
                      <span className="rh-user-name">{ev.employe?.prenom} {ev.employe?.nom}</span>
                    </div>
                  </td>
                  <td><strong>{ev.mois}/{ev.annee}</strong></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div className="rh-progress-container" style={{ width: '120px' }}>
                        <div className={`rh-progress-fill ${ev.scoreFinal >= 80 ? 'green' : ev.scoreFinal >= 50 ? 'orange' : 'red'}`} style={{ width: `${ev.scoreFinal}%` }} />
                      </div>
                      <span style={{ fontWeight: '700', fontSize: '13px' }}>{ev.scoreFinal}%</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} size={14} fill={s <= (ev.scoreFinal / 20) ? 'var(--rh-yellow)' : 'none'} color={s <= (ev.scoreFinal / 20) ? 'var(--rh-yellow)' : '#E2E8F0'} />
                      ))}
                    </div>
                  </td>
                  <td>
                    <span className="rh-status-badge approved">Finalisée</span>
                  </td>
                  <td>
                    <div className="rh-actions" style={{ justifyContent: 'flex-end' }}>
                      <button className="rh-action-btn edit" title="Détails" onClick={() => Swal.fire('Évaluation', ev.commentaireRH || 'Aucun détail.', 'info')}>
                        <Target size={16} />
                      </button>
                      <button className="rh-action-btn delete" onClick={() => handleDelete(ev._id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
